import React from "react";
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Box, Typography, useMediaQuery, useTheme, Stack, Chip,
} from "@mui/material";
import { CARD_SX } from "./AnalyticsPageShell";

export function StatusChip({ status }) {
  const s = String(status || "").toLowerCase();
  const map = {
    active: { bg: "#dcfce7", color: "#16a34a" },
    upcoming: { bg: "#dbeafe", color: "#2563eb" },
    overdue: { bg: "#fee2e2", color: "#dc2626" },
    pending: { bg: "#fef3c7", color: "#d97706" },
    high: { bg: "#fee2e2", color: "#dc2626" },
    medium: { bg: "#fef3c7", color: "#d97706" },
    low: { bg: "#e0e7ff", color: "#4f46e5" },
    "almost full": { bg: "#fff7ed", color: "#ea580c" },
    almost_full: { bg: "#fff7ed", color: "#ea580c" },
    on_track: { bg: "#dcfce7", color: "#16a34a" },
    "on track": { bg: "#dcfce7", color: "#16a34a" },
    at_risk: { bg: "#fef3c7", color: "#d97706" },
    "at risk": { bg: "#fef3c7", color: "#d97706" },
    not_started: { bg: "#f1f5f9", color: "#64748b" },
    "not started": { bg: "#f1f5f9", color: "#64748b" },
    due_soon: { bg: "#dbeafe", color: "#2563eb" },
    "due soon": { bg: "#dbeafe", color: "#2563eb" },
    normal: { bg: "#e0e7ff", color: "#4f46e5" },
    completed: { bg: "#f1f5f9", color: "#64748b" },
  };
  const style = map[s] || { bg: "#f1f5f9", color: "#475569" };
  return (
    <Chip
      label={status || "—"}
      size="small"
      sx={{ bgcolor: style.bg, color: style.color, fontWeight: 600, textTransform: "capitalize" }}
    />
  );
}

export default function AnalyticsDataTable({ columns, rows, loading, emptyMessage, renderActions }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!rows?.length) {
    return (
      <Paper sx={CARD_SX}>
        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
          {emptyMessage || "No data for the selected filters."}
        </Typography>
      </Paper>
    );
  }

  if (isMobile) {
    return (
      <Stack spacing={1.5}>
        {rows.map((row, idx) => (
          <Paper key={row.id ?? idx} sx={CARD_SX}>
            {columns.map((col) => (
              <Box key={col.key} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                <Typography variant="caption" color="text.secondary">{col.label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, textAlign: "right", maxWidth: "60%" }}>
                  {col.render ? col.render(row) : row[col.key]}
                </Typography>
              </Box>
            ))}
            {renderActions && <Box sx={{ pt: 1 }}>{renderActions(row)}</Box>}
          </Paper>
        ))}
      </Stack>
    );
  }

  return (
    <TableContainer component={Paper} sx={CARD_SX}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.key} sx={{ fontWeight: 700 }}>{col.label}</TableCell>
            ))}
            {renderActions && <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow key={row.id ?? idx} hover>
              {columns.map((col) => (
                <TableCell key={col.key}>
                  {col.render ? col.render(row) : (row[col.key] ?? "—")}
                </TableCell>
              ))}
              {renderActions && <TableCell>{renderActions(row)}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
